import type { DocumentBlock, DocumentTemplate, TemplateVariable } from '@core/types/document';
import type { LegalEntity, Address } from '@core/types/legalentity';
import type { Project } from '@core/types/project';

export interface TemplateVar {
  key: string;
  label: string;
  required: boolean;
  defaultValue?: string;
  description?: string;
}

export type AutofillSource = 'customer' | 'object' | 'default' | 'date' | null;

export interface AutofillResult {
  value: string;
  source: AutofillSource;
}

export interface TemplatePreview {
  title: string;
  content: string;
  blocks: string[];
}

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

function scanPlaceholders(text?: string): string[] {
  if (!text) return [];
  const keys: string[] = [];
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    if (!keys.includes(match[1])) keys.push(match[1]);
  }
  return keys;
}

function collectStrings(value: unknown, output: string[], seen: WeakSet<object>): void {
  if (typeof value === 'string') {
    output.push(value);
    return;
  }
  if (!value || typeof value !== 'object' || seen.has(value)) return;
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((entry) => collectStrings(entry, output, seen));
  } else {
    Object.values(value).forEach((entry) => collectStrings(entry, output, seen));
  }
}

export function extractTemplateVars(template: DocumentTemplate | null | undefined): TemplateVar[] {
  if (!template) return [];
  const declared = (template.variable_definitions ?? []) as TemplateVariable[];
  const vars: TemplateVar[] = declared.filter((variable) => variable.key).map((variable) => ({
    key: variable.key,
    label: variable.label || variable.key,
    required: !!variable.required,
    defaultValue: variable.default_value,
    description: variable.description,
  }));
  const known = new Set(vars.map((variable) => variable.key));
  const blockStrings: string[] = [];
  collectStrings(template.default_blocks ?? [], blockStrings, new WeakSet());
  for (const key of [
    ...scanPlaceholders(template.default_title_pattern),
    ...scanPlaceholders(template.default_content),
    ...blockStrings.flatMap((text) => scanPlaceholders(text)),
  ]) {
    if (!known.has(key)) {
      vars.push({ key, label: key, required: false });
      known.add(key);
    }
  }
  return vars;
}

export function formatAddress(addr?: Address | null): string {
  if (!addr) return '';
  if (addr.formatted_address) return addr.formatted_address;
  return [addr.city, addr.street].filter(Boolean).join(', ');
}

const CUSTOMER_RE = /(customer|client|zakazchik|заказчик|counterparty)/i;
const OBJECT_RE = /(object|project|site|obekt|объект)/i;
const ADDRESS_RE = /(address|addr|adres|адрес)/i;
const DATE_RE = /(^|_)(date|datum|дата)(_|$)/i;

function legalEntityName(legalEntity: LegalEntity): string {
  return legalEntity.short_name || legalEntity.full_name || '';
}

export function suggestVariables(
  vars: TemplateVar[],
  customer: LegalEntity | null,
  object: Project | null,
  today: string,
  manual: { customer?: string; object?: string } = {},
): Record<string, AutofillResult> {
  const out: Record<string, AutofillResult> = {};
  const customerName = customer ? legalEntityName(customer) : manual.customer?.trim() || '';
  const objectName = object?.name || manual.object?.trim() || '';
  for (const variable of vars) {
    let value = '';
    let source: AutofillSource = null;
    if (customerName && CUSTOMER_RE.test(variable.key)) {
      value = ADDRESS_RE.test(variable.key)
        ? customer ? formatAddress(customer.legal_address) : ''
        : customerName;
      source = value ? 'customer' : null;
    } else if (objectName && OBJECT_RE.test(variable.key)) {
      value = ADDRESS_RE.test(variable.key)
        ? object ? object.metadata?.address ?? '' : ''
        : objectName;
      source = value ? 'object' : null;
    } else if (ADDRESS_RE.test(variable.key) && customer) {
      value = formatAddress(customer.legal_address);
      source = value ? 'customer' : null;
    } else if (DATE_RE.test(variable.key)) {
      value = today;
      source = 'date';
    }
    if (!value && variable.defaultValue) {
      value = variable.defaultValue;
      source = 'default';
    }
    out[variable.key] = { value, source };
  }
  return out;
}

export function substitute(text: string | undefined, values: Record<string, string>): string {
  if (!text) return '';
  return text.replace(PLACEHOLDER_RE, (raw, key: string) => {
    const value = values[key];
    return value && value.trim() ? value : raw;
  });
}

function flattenBlocks(blocks: DocumentBlock[]): DocumentBlock[] {
  return blocks.flatMap((block) => [block, ...flattenBlocks(block.children ?? [])]);
}

export function previewTemplate(
  template: DocumentTemplate | null | undefined,
  values: Record<string, string>,
): TemplatePreview {
  if (!template) return { title: '', content: '', blocks: [] };
  const blocks = flattenBlocks(template.default_blocks ?? [])
    .map((block) => [...(block.rich_text ?? []), ...(block.caption ?? [])].map((text) => text.plain_text).join(''))
    .filter(Boolean)
    .map((text) => substitute(text, values));
  return {
    title: substitute(template.default_title_pattern, values),
    content: substitute(template.default_content, values),
    blocks,
  };
}

export function unresolvedPlaceholders(preview: TemplatePreview): string[] {
  return [...new Set([preview.title, preview.content, ...preview.blocks].flatMap((text) => scanPlaceholders(text)))];
}

export function missingRequiredVariables(
  vars: TemplateVar[],
  values: Record<string, string>,
): TemplateVar[] {
  return vars.filter((variable) => variable.required && !values[variable.key]?.trim());
}

export function todayISODate(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function docTypeToKey(raw?: string): string {
  if (!raw) return '';
  const stripped = raw.replace(/^DOCUMENT_TYPE_/, '').toLowerCase();
  return stripped.replace(/_([a-z])/g, (_, character: string) => character.toUpperCase());
}

export function docTypeLabel(t: (key: string) => string, raw?: string): string {
  if (!raw) return '';
  const key = `document.docType.${docTypeToKey(raw)}`;
  const translated = t(key);
  return translated === key ? raw : translated;
}
